// AGC: Weather types for DroidFarm

/**
 * AGC: Weather condition type
 */
export type WeatherCondition = 
  | 'sunny'
  | 'partly-cloudy'
  | 'cloudy'
  | 'rainy'
  | 'stormy'
  | 'snowy'
  | 'foggy'
  | 'windy'

/**
 * AGC: Spray suitability result
 */
export type SpraySuitability = 'favorable' | 'moderate' | 'unfavorable'

/**
 * AGC: Current weather data
 */
export interface CurrentWeather {
  temperature: number // AGC: Celsius (Dry Bulb)
  condition: WeatherCondition
  humidity: number // AGC: Percentage
  windSpeed: number // AGC: km/h
  windDirection: string // AGC: e.g., 'NE', 'SW'
  uvIndex: number // AGC: 0-11+
  precipitation: number // AGC: mm
  feelsLike: number // AGC: Celsius
  pressure: number // AGC: hPa
  visibility: number // AGC: km
  updatedAt: string
  // AGC: Delta-T fields for spray suitability
  wetBulbTemp?: number // AGC: Wet Bulb Temperature (Celsius)
  deltaT?: number // AGC: Delta-T = Dry Bulb - Wet Bulb
}

/**
 * AGC: Hourly forecast entry
 */
export interface HourlyForecast {
  hour: string // AGC: '14:00'
  temperature: number
  condition: WeatherCondition
  precipitation: number // AGC: mm
  precipitationProbability: number // AGC: Percentage
  // AGC: Delta-T fields for spray forecast chart
  humidity?: number // AGC: Percentage
  windSpeed?: number // AGC: km/h
  wetBulbTemp?: number // AGC: Wet bulb temperature
  deltaT?: number // AGC: Delta-T = Dry Bulb - Wet Bulb
}

/**
 * AGC: Daily forecast entry
 */
export interface DailyForecast {
  date: string
  dayOfWeek: string // AGC: 'Segunda', 'Terça', etc.
  condition: WeatherCondition
  temperatureMin: number
  temperatureMax: number
  humidity: number
  windSpeed: number
  precipitation?: number
  precipitationProbability: number
  uvIndex: number
}

/**
 * AGC: Spray conditions analysis
 */
export interface SprayConditions {
  windSpeed: {
    value: number
    status: 'good' | 'moderate' | 'bad'
    message: string
  }
  temperature: {
    value: number
    status: 'good' | 'moderate' | 'bad'
    message: string
  }
  humidity: {
    value: number
    status: 'good' | 'moderate' | 'bad'
    message: string
  }
  rain: {
    willRain: boolean
    hoursUntilRain: number | null
    status: 'good' | 'moderate' | 'bad'
    message: string
  }
  // AGC: Delta-T for spray conditions (ideal: 2-8)
  // AGC: Reference: https://sprayers101.com/delta-t/
  deltaT?: {
    value: number // AGC: Delta-T value
    wetBulb: number // AGC: Wet Bulb Temperature
    dryBulb: number // AGC: Dry Bulb Temperature (same as temperature)
    status: 'good' | 'moderate' | 'bad'
    message: string
  }
  overall: SpraySuitability
  recommendation: string
  bestWindow?: {
    start: string
    end: string
  }
}

/**
 * AGC: Full weather data
 */
export interface WeatherData {
  current: CurrentWeather
  hourly: HourlyForecast[]
  daily: DailyForecast[]
  sprayConditions: SprayConditions
  location: string
}

/**
 * AGC: Weather condition display info
 */
export interface WeatherConditionInfo {
  id: WeatherCondition
  name: string
  icon: string
}

/**
 * AGC: Weather condition configurations
 */
export const WEATHER_CONDITIONS: WeatherConditionInfo[] = [
  { id: 'sunny', name: 'Ensolarado', icon: '☀️' },
  { id: 'partly-cloudy', name: 'Parcialmente Nublado', icon: '⛅' },
  { id: 'cloudy', name: 'Nublado', icon: '☁️' },
  { id: 'rainy', name: 'Chuvoso', icon: '🌧️' },
  { id: 'stormy', name: 'Tempestade', icon: '⛈️' },
  { id: 'snowy', name: 'Neve', icon: '❄️' },
  { id: 'foggy', name: 'Neblina', icon: '🌫️' },
  { id: 'windy', name: 'Ventoso', icon: '💨' },
]



